import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PriceDisplay from '@/components/PriceDisplay';
import KoryuScrollBanner from '@/components/KoryuScrollBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrowsePrograms = () => {
  const [branding, setBranding] = useState({ logo_url: null, site_name: 'Train In Japan' });
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [koryuOnly, setKoryuOnly] = useState(false);

  useEffect(() => {
    fetchPrograms();
    axios.get(`${API}/branding/logo`).then(res => setBranding(res.data)).catch(err => console.error('Branding error:', err));
  }, []);

  useEffect(() => {
    applyFilters();
  }, [selectedCategory, selectedLevel, searchQuery, maxPrice, koryuOnly, programs]);

  const applyFilters = () => {
    let filtered = programs;

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    // Experience level filter
    if (selectedLevel !== 'all') {
      filtered = filtered.filter(p => p.experience_level === selectedLevel);
    }

    // Search query (title, description, martial_arts_style)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.title.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query) ||
        p.martial_arts_style.toLowerCase().includes(query)
      );
    }

    // Max price filter
    if (maxPrice) {
      filtered = filtered.filter(p => p.price <= parseFloat(maxPrice));
    }

    // Koryū filter
    if (koryuOnly) {
      filtered = filtered.filter(p => p.is_koryu === true);
    }

    // Randomize the order to give all programs equal visibility
    const randomized = [...filtered].sort(() => Math.random() - 0.5);
    setFilteredPrograms(randomized);
  };

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API}/programs`);
      setPrograms(response.data);
      setFilteredPrograms(response.data);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', 'Martial Arts', 'Cultural Arts', 'Sword Arts', 'Archery', 'Meditation'];

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 to-emerald-50">
      {/* Floating Logo - Hidden on mobile to avoid overlap */}
      <div className="hidden md:fixed md:block top-3 left-6 z-[60] cursor-pointer" onClick={() => navigate('/')}>
        <div className="flex flex-col items-center" style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))' }}>
          {branding.logo_url && (
            <div className="w-20 h-20 rounded-full bg-white border-[3px] border-emerald-600 overflow-hidden">
              <img src={branding.logo_url} alt="Logo" className="w-full h-full object-cover" />
            </div>
          )}
          <span className="mt-1 text-base font-bold text-slate-800 bg-white/90 px-2 py-0.5 rounded-full" style={{ fontFamily: 'Playfair Display, serif' }}>{branding.site_name}</span>
        </div>
      </div>

      {/* Navigation - Mobile optimized */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          {/* Mobile: Show clickable site name */}
          <div className="md:hidden flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            {branding.logo_url && (
              <div className="w-8 h-8 rounded-full bg-white border-2 border-emerald-600 overflow-hidden flex-shrink-0">
                <img src={branding.logo_url} alt="Logo" className="w-full h-full object-cover" />
              </div>
            )}
            <span className="text-sm font-semibold text-slate-800 truncate" style={{ fontFamily: 'Playfair Display, serif' }}>{branding.site_name}</span>
          </div>
          {/* Desktop: Spacer for floating logo */}
          <div className="hidden md:block w-12"></div>
          <div className="flex items-center gap-4 md:gap-6">
            <button onClick={() => navigate('/')} className="text-sm md:text-base text-slate-700 hover:text-emerald-800 font-medium transition-colors">Home</button>
            {user && (
              <Button data-testid="nav-dashboard-btn" onClick={() => navigate('/dashboard')} variant="default" size="sm" className="bg-emerald-700 hover:bg-emerald-800">Dashboard</Button>
            )}
          </div>
        </div>
      </nav>

      {/* Header - Mobile optimized */}
      <section className="pt-16 pb-8 sm:py-16 px-4 sm:px-6 bg-gradient-to-r from-emerald-700 to-emerald-800">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-2 sm:mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Browse Programs</h1>
          <p className="text-emerald-100 text-sm sm:text-base md:text-lg">Discover authentic Japanese martial arts and cultural training programs</p>
        </div>
      </section>

      {/* Filters - Mobile optimized */}
      <section data-testid="programs-section" className="py-4 sm:py-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <Card className="p-4 sm:p-6 mb-6 sm:mb-8">
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-3 sm:mb-4">Filter Programs</h3>
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {/* Search */}
              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1 sm:mb-2">Search</label>
                <input
                  data-testid="search-input"
                  type="text"
                  placeholder="Search programs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1 sm:mb-2">Category</label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger data-testid="category-filter" className="w-full text-sm">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat === 'all' ? 'All' : cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Experience Level */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1 sm:mb-2">Level</label>
                <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                  <SelectTrigger data-testid="level-filter" className="w-full text-sm">
                    <SelectValue placeholder="Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Max Price */}
              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1 sm:mb-2">Max Price (AUD)</label>
                <input
                  data-testid="price-filter"
                  type="number"
                  placeholder="Any price"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Koryū Filter - Compact on mobile */}
            <div className="flex items-center space-x-2 p-2 sm:p-3 mt-3 bg-amber-50 border border-amber-200 rounded-lg">
              <input
                type="checkbox"
                id="koryuOnly"
                checked={koryuOnly}
                onChange={(e) => setKoryuOnly(e.target.checked)}
                className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
              />
              <label htmlFor="koryuOnly" className="text-xs sm:text-sm font-medium text-slate-700 cursor-pointer select-none">
                Show Koryū Only 古流
              </label>
            </div>
            
            {/* Koryū Legend */}
            {koryuOnly && (
              <div className="p-3 sm:p-4 mt-3 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-600 rounded-lg">
                <h3 className="text-xs sm:text-sm font-bold text-amber-900 mb-1 sm:mb-2 flex items-center">
                  <span className="mr-2">📜</span>
                  What is Koryū? (古流)
                </h3>
                <p className="text-xs text-amber-800 leading-relaxed">
                  Koryū (古流) refers to "old schools" of Japanese martial arts established before 1868. 
                  These are living traditions preserving combat systems exactly as they were taught centuries ago, 
                  offering a direct connection to samurai-era Japan.
                </p>
              </div>
            )}

            {/* Results count */}
            <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-slate-600">
              Showing {filteredPrograms.length} of {programs.length} programs
            </div>
          </Card>

          {loading ? (
            <div className="text-center py-12 sm:py-20">
              <div className="inline-block animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-emerald-700"></div>
            </div>
          ) : filteredPrograms.length === 0 ? (
            <div data-testid="no-programs" className="text-center py-12 sm:py-20">
              <p className="text-slate-600 text-base sm:text-lg">No programs available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {filteredPrograms.map((program, idx) => (
                <Card key={program.id} data-testid={`program-card-${idx}`} className="overflow-hidden hover:shadow-xl transition-all duration-300 group">
                  {/* Banner/Image Section with School Branding Overlay */}
                  <div className="relative">
                    {/* Always use program image - school branding shown via logo overlay */}
                    <div className="relative h-40 sm:h-48 overflow-hidden">
                      {program.image_url ? (
                        <img 
                          src={program.image_url.startsWith('/api') ? `${BACKEND_URL}${program.image_url}` : program.image_url} 
                          alt={program.title} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                          <span className="text-slate-500 text-sm">No Image</span>
                        </div>
                      )}
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      
                      {/* School Logo - Prominent Position */}
                      {program.school && (
                        <div className="absolute top-2 sm:top-3 left-2 sm:left-3 flex items-center gap-1.5 sm:gap-2 bg-white/95 backdrop-blur-sm rounded-full pr-2 sm:pr-3 shadow-lg">
                          {program.school.logo_url ? (
                            <img 
                              src={program.school.logo_url.startsWith('/api') ? `${BACKEND_URL}${program.school.logo_url}` : program.school.logo_url}
                              alt={program.school.name}
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-emerald-500"
                            />
                          ) : (
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald-600 flex items-center justify-center border-2 border-emerald-500">
                              <span className="text-white font-bold text-xs sm:text-sm">
                                {program.school.name.charAt(0)}
                              </span>
                            </div>
                          )}
                          <span className="text-xs sm:text-sm font-semibold text-slate-800 max-w-[80px] sm:max-w-[120px] truncate">
                            {program.school.name}
                          </span>
                        </div>
                      )}
                      
                      {/* Koryu Badge - moved to bottom right */}
                      {program.is_koryu && (
                        <div className="absolute bottom-12 sm:bottom-14 right-2 sm:right-3 z-10">
                          <KoryuScrollBanner size="sm" />
                        </div>
                      )}
                      
                      {/* Month/Year Badge */}
                      {program.start_date && program.start_date !== "TBD" && (
                        <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 bg-slate-900/80 backdrop-blur-sm text-white px-2 sm:px-3 py-1 rounded-lg shadow-lg" style={{ zIndex: program.is_koryu ? 5 : 10 }}>
                          <span className="text-xs sm:text-sm font-bold">
                            {new Date(program.start_date).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                          </span>
                        </div>
                      )}
                      
                      {/* Program Title on Banner */}
                      <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 right-2 sm:right-3">
                        <h3 className="text-base sm:text-lg font-bold text-white drop-shadow-lg line-clamp-2" style={{ fontFamily: 'Spectral, serif' }}>
                          {program.title}
                        </h3>
                      </div>
                    </div>
                  </div>
                  
                  <CardHeader className="pt-3 sm:pt-4 pb-2 px-3 sm:px-6">
                    {/* Category Badge */}
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2">
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full">
                        {program.martial_arts_style || program.category}
                      </span>
                      <span className="text-xs font-medium text-slate-500 bg-slate-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                        {program.experience_level}
                      </span>
                    </div>
                    
                    <CardDescription className="line-clamp-2 text-sm text-slate-600">{program.description}</CardDescription>
                  </CardHeader>
                  
                  <CardContent className="pt-0 px-3 sm:px-6">
                    <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Duration</span>
                        <span className="font-medium text-slate-700">{program.duration}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Price</span>
                        <PriceDisplay price={program.price} currency={program.currency} size="md" showWithFees={true} />
                      </div>
                      {program.start_date && program.start_date !== 'TBD' && (
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500">Starts</span>
                          <span className="font-medium text-slate-700">{program.start_date}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                  
                  <CardFooter className="pt-0 px-3 sm:px-6 pb-3 sm:pb-6">
                    <Button 
                      data-testid={`view-program-${idx}`}
                      onClick={() => navigate(`/programs/${program.id}`)} 
                      className="w-full bg-emerald-700 hover:bg-emerald-800 text-sm sm:text-base"
                    >
                      View Details
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BrowsePrograms;
